import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getApiUrl, addUserParam } from '../config/api.config';
import { marketSession } from '../utils/session';

// NOTE: Do NOT call getApiUrl() at module load time!
// It must be called at request time when window.location is available.

// ============================================
// CLIENT-SIDE CACHE for historical data
// Makes clicking cells INSTANT after first load
// ============================================
const historyCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const getCacheKey = (ticker1, ticker2, granularity, days) => {
  const [t1, t2] = [ticker1, ticker2].sort();
  return `${t1}-${t2}:${granularity}:${days}`;
};

const getFromCache = (key) => {
  const entry = historyCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiry) {
    historyCache.delete(key);
    return null;
  }
  return entry.data;
};

const setInCache = (key, data) => {
  historyCache.set(key, {
    data,
    expiry: Date.now() + CACHE_TTL
  });
  // Limit cache size to 50 entries
  if (historyCache.size > 50) {
    const firstKey = historyCache.keys().next().value;
    historyCache.delete(firstKey);
  }
};

// Helper to invert spread data (for B-A when cached as A-B)
const invertSpreadData = (data) => {
  if (!data) return data;

  const inverted = { ...data };

  // Invert historical array if present
  if (inverted.historical && Array.isArray(inverted.historical)) {
    inverted.historical = inverted.historical.map(point => ({
      ...point,
      dollarSpread: point.dollarSpread !== undefined ? -point.dollarSpread : undefined,
      yieldSpread: point.yieldSpread !== undefined ? -point.yieldSpread : undefined,
      ytcSpread: point.ytcSpread !== undefined ? -point.ytcSpread : undefined,
      ytmSpread: point.ytmSpread !== undefined ? -point.ytmSpread : undefined,
      spotYieldSpread: point.spotYieldSpread !== undefined ? -point.spotYieldSpread : undefined,
      // Swap ticker1/ticker2 data
      ticker1Price: point.ticker2Price,
      ticker1Yield: point.ticker2Yield,
      ticker1YTC: point.ticker2YTC,
      ticker1SpotYield: point.ticker2SpotYield,
      ticker2Price: point.ticker1Price,
      ticker2Yield: point.ticker1Yield,
      ticker2YTC: point.ticker1YTC,
      ticker2SpotYield: point.ticker1SpotYield,
    }));
  }

  // Invert top-level spread values if present
  if (inverted.dollarSpread !== undefined) inverted.dollarSpread = -inverted.dollarSpread;
  if (inverted.yieldSpread !== undefined) inverted.yieldSpread = -inverted.yieldSpread;
  if (inverted.ytcSpread !== undefined) inverted.ytcSpread = -inverted.ytcSpread;
  if (inverted.ytmSpread !== undefined) inverted.ytmSpread = -inverted.ytmSpread;
  if (inverted.spotYieldSpread !== undefined) inverted.spotYieldSpread = -inverted.spotYieldSpread;
  if (inverted.spread !== undefined) inverted.spread = -inverted.spread;
  if (inverted.percentile !== undefined) inverted.percentile = 100 - inverted.percentile;

  return inverted;
};

// Fetch spread data for a ticker pair (with client-side caching)
export const fetchSpreads = createAsyncThunk(
  'spreads/fetchSpreads',
  async ({ ticker1, ticker2, granularity, historical = false, days = 7 }, { rejectWithValue }) => {
    const pairKey = [ticker1, ticker2].sort().join('-');

    // Determine if we need to invert (requested B-A but stored as A-B)
    const [sortedT1] = [ticker1, ticker2].sort();
    const needsInvert = sortedT1 !== ticker1;

    // Check cache first for historical data (INSTANT response)
    if (historical) {
      const cacheKey = getCacheKey(ticker1, ticker2, granularity, days);
      const cached = getFromCache(cacheKey);
      if (cached) {
        // Invert if requested order differs from cached order
        const result = needsInvert ? invertSpreadData(cached) : cached;
        return {
          pairKey,
          ...result,
          fromCache: true
        };
      }
    }

    try {
      const params = new URLSearchParams({
        ticker1,
        ticker2,
        granularity,
        historical: historical.toString(),
        days: days.toString()
      });

      const response = await fetch(
        addUserParam(`${getApiUrl()}/spreads?${params}`),
        {
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch spread data');
      }

      const data = await response.json();

      // Cache historical data for instant future access
      if (historical && data.historical) {
        const cacheKey = getCacheKey(ticker1, ticker2, granularity, days);
        setInCache(cacheKey, data);
      }

      return {
        pairKey,
        ...data
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Legacy async thunk for backward compatibility
export const getSpreadData = createAsyncThunk(
  'spreads/fetchData',
  async ({ ticker1, ticker2, granularity, timeRange }) => {
    const response = await fetch(
      addUserParam(`${getApiUrl()}/spreads?ticker1=${ticker1}&ticker2=${ticker2}&granularity=${granularity}&timeRange=${timeRange}`),
      {
        credentials: 'include'
      }
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch spread data');
    }
    
    return response.json();
  }
);

export const getSpreadMatrix = createAsyncThunk(
  'spreads/fetchMatrix',
  async ({ tickers, granularity }) => {
    const response = await fetch(
      addUserParam(`${getApiUrl()}/spreads/matrix`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ tickers, granularity })
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch spread matrix');
    }

    return response.json();
  }
);

// ============================================
// CLIENT-SIDE CACHE for matrix data
// ============================================
const matrixCache = new Map();
const MATRIX_CACHE_TTL = 3 * 60 * 1000; // 3 minutes

const getMatrixCacheKey = (tickers, granularity, lookbackDays = 180) => {
  return `${tickers.slice().sort().join(',')}:${granularity}:${lookbackDays}d`;
};

const getMatrixFromCache = (key) => {
  const entry = matrixCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiry) {
    matrixCache.delete(key);
    return null;
  }
  return entry.data;
};

const setMatrixInCache = (key, data) => {
  matrixCache.set(key, {
    data,
    expiry: Date.now() + MATRIX_CACHE_TTL
  });
  // Limit cache size to 20 entries
  if (matrixCache.size > 20) {
    const firstKey = matrixCache.keys().next().value;
    matrixCache.delete(firstKey);
  }
};

// Clear matrix cache for a specific list (called after reanalyze)
const clearMatrixCache = (tickers, granularity, lookbackDays) => {
  const key = getMatrixCacheKey(tickers, granularity, lookbackDays);
  matrixCache.delete(key);
};

// Clear all matrix cache entries for given tickers (any granularity)
const clearMatrixCacheForTickers = (tickers) => {
  const sortedTickers = tickers.slice().sort().join(',');
  for (const key of matrixCache.keys()) {
    if (key.startsWith(sortedTickers + ':')) {
      matrixCache.delete(key);
    }
  }
};

// Clear history cache for tickers (called after reanalyze)
const clearHistoryCacheForTickers = (tickers) => {
  for (const key of historyCache.keys()) {
    // Check if any of the tickers are in this cache key
    for (const ticker of tickers) {
      if (key.includes(ticker)) {
        historyCache.delete(key);
        break;
      }
    }
  }
};

// UI spread type -> the cell field whose percentile curve the server ships.
// Dollar has none (its percentile is a server-side synthetic measure).
export const CURVE_FIELD_BY_SPREAD_TYPE = { ytc: 'ytc_spread', 'spot-yield': 'ytm_spread' };

// The server sends each pair's curve, and its mean/std (the Med Z column), only
// on the alphabetical (a < b) cell. The mirror cell's curve is that one reversed
// and negated; its mean is negated and its std unchanged.
const restoreMirroredCurves = (matrix, field) => {
  if (!matrix || !field) return;
  for (const a of Object.keys(matrix)) {
    const row = matrix[a];
    if (!row) continue;
    for (const b of Object.keys(row)) {
      if (!(a < b)) continue;
      const srcField = row[b]?.[field];
      const dst = matrix[b]?.[a]?.[field];
      if (srcField && dst && srcField.mean != null && srcField.std != null && dst.mean == null) {
        dst.mean = -srcField.mean;
        dst.std = srcField.std;
      }
      const src = srcField?.curve;
      if (!src || !dst || dst.curve) continue;
      const n = src.length;
      const out = new Array(n);
      for (let i = 0; i < n; i++) {
        const v = src[n - 1 - i];
        out[i] = v == null ? null : -v;
      }
      dst.curve = out;
    }
  }
};

// Fetch spread matrix with trading levels (with client-side caching)
// lookbackDays: 90 (3M), 180 (6M), or 365 (1Y) - affects percentile computation
export const fetchMatrixWithLevels = createAsyncThunk(
  'spreads/fetchMatrixWithLevels',
  async ({ tickers, granularity, listName, lookbackDays = 180, scalingFactors = {}, spreadType = null }, { rejectWithValue }) => {
    // Cache key includes scalingFactors so different ratios get cached separately
    const sfKey = Object.keys(scalingFactors).sort().map(k => `${k}=${scalingFactors[k]}`).join(',');
    // spreadType is part of the key: the response carries the percentile curve
    // for that type only, so switching type is a different payload.
    const cacheKey = getMatrixCacheKey(tickers, granularity, lookbackDays) + (sfKey ? `:${sfKey}` : '') + (spreadType ? `:st=${spreadType}` : '');
    const cached = getMatrixFromCache(cacheKey);
    if (cached) {
      return {
        listName,
        ...cached,
        fromCache: true
      };
    }

    try {
      const response = await fetch(
        addUserParam(`${getApiUrl()}/spreads/matrix-with-levels`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ tickers, granularity, lookbackDays, scalingFactors, spreadType })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch matrix with levels');
      }

      const data = await response.json();
      restoreMirroredCurves(data.matrix, CURVE_FIELD_BY_SPREAD_TYPE[spreadType]);
      // Shallow-freeze: Immer skips frozen objects, so the store no longer
      // deep-walks every cell of a multi-MB matrix (~114 ms per list measured).
      // Nothing mutates these cells; the matrices copy before changing a value.
      if (data.matrix) Object.freeze(data.matrix);

      // When (browser clock) and in which market session these prices were
      // obtained. Stamped before caching, so a cache hit keeps the time of the
      // fetch, not of the hit. The Dashboard compares it with the stamp on
      // streamed prices and names what the price is (last / prev close / close).
      data.fetchedAt = Date.now();
      data.fetchedSession = marketSession(data.fetchedAt);

      // Cache the matrix data for faster subsequent loads
      setMatrixInCache(cacheKey, data);

      return {
        listName,
        ...data
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Each ticker's Med% at a past session close, for the matrix's Δ Med% column.
// `requestKey` identifies the settings it was computed for, so the Dashboard
// can tell a stale reference from a current one.
export const fetchMedPercReference = createAsyncThunk(
  'spreads/fetchMedPercReference',
  async ({ listName, requestKey, tickers, granularity, lookbackDays, scalingFactors = {}, sessionsBack = 1 }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        addUserParam(`${getApiUrl()}/spreads/med-perc-reference`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ tickers, granularity, lookbackDays, scalingFactors, sessionsBack })
        }
      );
      if (!response.ok) throw new Error('Failed to fetch reference Med%');
      const data = await response.json();
      return { listName, requestKey, ...data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch trading levels for a specific pair
export const fetchTradingLevels = createAsyncThunk(
  'spreads/fetchTradingLevels',
  async ({ ticker1, ticker2, granularity }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        addUserParam(`${getApiUrl()}/spreads/levels/${ticker1}/${ticker2}?granularity=${granularity}`),
        {
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch trading levels');
      }

      const data = await response.json();
      return {
        pairKey: [ticker1, ticker2].sort().join('-'),
        ...data
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Reanalyze spreads for a list
// Set force=true to force full recalculation, otherwise incremental (default)
// Incremental mode only computes missing pairs, which is much faster
export const reanalyzeSpreads = createAsyncThunk(
  'spreads/reanalyze',
  async ({ listName, tickers, granularity = '1h', force = false }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        addUserParam(`${getApiUrl()}/spreads/reanalyze`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ listName, tickers, granularity, force })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reanalyze spreads');
      }

      const data = await response.json();

      // Only clear caches if calculation actually ran (not skipped)
      if (!data.skipped) {
        clearMatrixCacheForTickers(tickers);
        clearHistoryCacheForTickers(tickers);
      }

      return {
        listName,
        tickers,
        ...data
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Check data freshness (compare MongoDB vs ClickHouse dates)
export const checkDataFreshness = createAsyncThunk(
  'spreads/checkDataFreshness',
  async (_, { rejectWithValue }) => {
    try {
      const apiUrl = getApiUrl();
      // Note: getApiUrl() returns URL ending in /api, so use /spreads/... not /api/spreads/...
      const response = await fetch(addUserParam(`${apiUrl}/spreads/data-freshness`), {
        credentials: 'include'
      });

      // Check content-type to handle HTML responses (auth redirects)
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // Silently fail for freshness check - not critical
        return { status: {}, needsUpdate: false, checkedAt: new Date().toISOString() };
      }

      if (!response.ok) {
        throw new Error('Failed to check data freshness');
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Trigger data sync (update ClickHouse with latest MongoDB data)
// Uses incremental mode - only computes missing dates/times
//
// The server starts the run in the background and answers at once: 202 with
// the new job, 409 with the job already running (any sync, or the nightly
// run: this request joins it), or 200 skipped. The Dashboard then polls
// fetchSyncStatus until the job ends. Awaiting the 16-40 min run here is what
// the proxy's 90 s timeout used to turn into "Server returned invalid response".
export const syncData = createAsyncThunk(
  'spreads/syncData',
  async ({ granularity = 'all', scope = 'all', tickers = null } = {}, { rejectWithValue }) => {
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(addUserParam(`${apiUrl}/spreads/sync-data`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ granularity, incremental: true, scope, tickers })
      });

      // Check content-type to handle HTML responses (auth redirects)
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        if (response.status === 401) {
          throw new Error('Authentication required. Please log in again.');
        }
        throw new Error('Server returned invalid response. Check if backend is running.');
      }

      const data = await response.json();
      if (response.status === 409) return { ...data, joined: true };
      if (!response.ok) {
        throw new Error(data.details || data.error || 'Sync failed');
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// The running sync (whoever started it), else the last one.
export const fetchSyncStatus = createAsyncThunk(
  'spreads/fetchSyncStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(addUserParam(`${getApiUrl()}/spreads/sync-status`), {
        credentials: 'include'
      });
      const contentType = response.headers.get('content-type');
      if (!response.ok || !contentType || !contentType.includes('application/json')) {
        throw new Error('Failed to read sync status');
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// After a sync: cached matrices and chart histories predate the new data.
export const clearClientSpreadCaches = () => {
  matrixCache.clear();
  historyCache.clear();
};

// Spreads slice
const spreadsSlice = createSlice({
  name: 'spreads',
  initialState: {
    spreads: {}, // Store spreads by pair key (e.g., "ABC-XYZ")
    currentSpread: null,
    spreadHistory: [],
    matrix: {},
    matrixWithLevels: {}, // NEW: Store matrix data by list name
    medPercReference: {}, // listName -> { requestKey, referenceSession, medPerc: {dollar, ytc, 'spot-yield'} }
    tradingLevels: {}, // NEW: Store trading levels by pair key
    yields: {}, // NEW: Store yields by ticker
    selectedGranularity: '1d',  // Default to daily granularity
    selectedLookbackDays: 180,    // Default to 6 months (180 days) for matrix percentiles
    selectedTimeRange: '-7d',
    loading: false,
    matrixLoading: false,
    reanalyzing: false, // NEW: Track reanalyze status
    reanalyzeError: null, // NEW: Track reanalyze errors
    // Data sync state
    dataFreshness: null,
    dataSyncing: false,
    dataSyncError: null,
    syncJob: null, // latest /sync-status (or sync-data) answer: { jobId, state, progress, ... }
    error: null,
  },
  reducers: {
    setGranularity: (state, action) => {
      state.selectedGranularity = action.payload;
      // Clear matrix data to force refetch with new granularity
      state.matrixWithLevels = {};
      state.medPercReference = {};
      // Client caches are keyed by granularity and lookback, so they stay:
      // toggling back re-uses them instead of re-downloading every list.
    },
    setLookbackDays: (state, action) => {
      state.selectedLookbackDays = action.payload;
      // Clear matrix data to force refetch with new lookback period
      state.matrixWithLevels = {};
      state.medPercReference = {};
      // Client caches are keyed by granularity and lookback, so they stay:
      // toggling back re-uses them instead of re-downloading every list.
    },
    setTimeRange: (state, action) => {
      state.selectedTimeRange = action.payload;
    },
    clearSpreadData: (state) => {
      state.currentSpread = null;
      state.spreadHistory = [];
      state.error = null;
    },
    updateSpreadData: (state, action) => {
      // Update spreads with real-time WebSocket data (Object.assign for better performance)
      Object.assign(state.spreads, action.payload);
    },
    // A sync that changed data has ended: drop the matrices so the open lists
    // refetch (the server cleared its caches before reporting completion).
    syncFinished: (state) => {
      state.matrixWithLevels = {};
      state.medPercReference = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch spreads
      .addCase(fetchSpreads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSpreads.fulfilled, (state, action) => {
        state.loading = false;
        const { pairKey, ...spreadData } = action.payload;
        state.spreads[pairKey] = spreadData;
        
        // If historical data was requested, store it in spreadHistory
        if (action.payload.historical) {
          state.spreadHistory = action.payload.historical;
        }
      })
      .addCase(fetchSpreads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch spread data (legacy)
      .addCase(getSpreadData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSpreadData.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSpread = action.payload.stats;
        state.spreadHistory = action.payload.data;
      })
      .addCase(getSpreadData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch matrix data
      .addCase(getSpreadMatrix.pending, (state) => {
        state.matrixLoading = true;
        state.error = null;
      })
      .addCase(getSpreadMatrix.fulfilled, (state, action) => {
        state.matrixLoading = false;
        state.matrix = action.payload.matrix;
      })
      .addCase(getSpreadMatrix.rejected, (state, action) => {
        state.matrixLoading = false;
        state.error = action.error.message;
      })
      // Fetch matrix with levels
      .addCase(fetchMatrixWithLevels.pending, (state) => {
        state.matrixLoading = true;
        state.error = null;
      })
      .addCase(fetchMatrixWithLevels.fulfilled, (state, action) => {
        state.matrixLoading = false;
        const { listName, matrix, yields, tickers, granularity, timestamp } = action.payload;
        state.matrixWithLevels[listName] = {
          matrix,
          yields,
          tickers,
          granularity,
          timestamp,
          // Which curve (if any) this payload carries — see CURVE_FIELD_BY_SPREAD_TYPE.
          spreadType: action.meta.arg.spreadType || null,
          // Settings it was fetched for; the Dashboard refetches when these differ.
          requestSig: action.meta.arg.requestSig || null,
        };
        // Also update the yields store, each entry carrying the stamp above.
        if (yields && Object.keys(yields).length > 0) {
          const { fetchedAt, fetchedSession } = action.payload;
          for (const ticker of Object.keys(yields)) {
            state.yields[ticker] = yields[ticker]
              ? { ...yields[ticker], fetchedAt, fs: fetchedSession }
              : yields[ticker];
          }
        }
      })
      .addCase(fetchMatrixWithLevels.rejected, (state, action) => {
        state.matrixLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchMedPercReference.fulfilled, (state, action) => {
        const { listName, requestKey, currentSession, referenceSession, medPerc } = action.payload;
        state.medPercReference[listName] = { requestKey, currentSession, referenceSession, medPerc };
      })
      // Fetch trading levels for a pair
      .addCase(fetchTradingLevels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTradingLevels.fulfilled, (state, action) => {
        state.loading = false;
        const { pairKey, levels, statistics, computed_at, data_points, lookback_days } = action.payload;
        state.tradingLevels[pairKey] = {
          levels,
          statistics,
          computed_at,
          data_points,
          lookback_days
        };
      })
      .addCase(fetchTradingLevels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reanalyze spreads
      .addCase(reanalyzeSpreads.pending, (state) => {
        state.reanalyzing = true;
        state.reanalyzeError = null;
      })
      .addCase(reanalyzeSpreads.fulfilled, (state, action) => {
        state.reanalyzing = false;
        const { listName, skipped } = action.payload;
        // Only clear cached matrix if calculation actually ran (not skipped)
        if (!skipped && state.matrixWithLevels[listName]) {
          delete state.matrixWithLevels[listName];
        }
      })
      .addCase(reanalyzeSpreads.rejected, (state, action) => {
        state.reanalyzing = false;
        state.reanalyzeError = action.payload;
      })
      // Check data freshness
      .addCase(checkDataFreshness.pending, (state) => {
        state.dataSyncError = null;
      })
      .addCase(checkDataFreshness.fulfilled, (state, action) => {
        state.dataFreshness = action.payload;
      })
      .addCase(checkDataFreshness.rejected, (state, action) => {
        state.dataSyncError = action.payload;
      })
      // Sync data: the POST only starts (or joins) a run
      .addCase(syncData.pending, (state) => {
        state.dataSyncing = true;
        state.dataSyncError = null;
      })
      .addCase(syncData.fulfilled, (state, action) => {
        if (action.payload.state === 'running') {
          state.syncJob = action.payload;
          return;
        }
        state.dataSyncing = false;
        if (action.payload.afterSync) state.dataFreshness = action.payload.afterSync;
      })
      .addCase(syncData.rejected, (state, action) => {
        state.dataSyncing = false;
        state.dataSyncError = action.payload;
      })
      .addCase(fetchSyncStatus.fulfilled, (state, action) => {
        state.syncJob = action.payload;
        state.dataSyncing = action.payload.state === 'running';
      })
      .addCase(fetchSyncStatus.rejected, (state) => {
        // e.g. the backend restarting mid-run: a new object keeps the poll
        // going; the run itself carries on and the next answer picks it up.
        if (state.syncJob?.state === 'running') state.syncJob = { ...state.syncJob, pollFailedAt: Date.now() };
      });
  },
});

export const { setGranularity, setLookbackDays, setTimeRange, clearSpreadData, updateSpreadData, syncFinished } = spreadsSlice.actions;
export default spreadsSlice.reducer;